import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { CreditCard, Smartphone, Landmark, Wallet, ShieldCheck, ReceiptText, Info } from "lucide-react";

const METHODS = [
  { icon: CreditCard, label: "Credit / Debit Card", note: "Visa, Mastercard, RuPay, Amex" },
  { icon: Smartphone, label: "UPI", note: "GPay, PhonePe, Paytm, BHIM, any UPI app" },
  { icon: Landmark, label: "Net Banking", note: "50+ Indian banks via Razorpay" },
  { icon: Wallet, label: "Wallets", note: "Paytm, Amazon Pay, Mobikwik, Freecharge" },
];

export default function PaymentPolicy() {
  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="payment-policy-page">
      <Header />
      <main className="flex-1">
        <section className="bg-primary text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 md:py-14">
            <p className="text-accent text-sm font-semibold tracking-wider uppercase mb-3">Legal</p>
            <h1 className="font-display text-4xl md:text-5xl font-bold">Payment Policy</h1>
            <p className="text-white/70 mt-3 text-sm">Last updated: January 2026</p>
          </div>
        </section>

        <article className="max-w-4xl mx-auto px-4 sm:px-6 py-10 md:py-14 prose prose-slate max-w-none">
          <p className="text-ink/80 leading-relaxed">
            Everything you need to know about how money moves through The Pataudi — what we accept, how it is priced, and
            how your invoice and receipt work. All payments happen in <strong>Indian Rupees (INR ₹)</strong> through Razorpay,
            our PCI-DSS certified payments partner. We never see or store your card number.
          </p>

          <h2 className="font-display text-2xl font-bold text-ink mt-8 mb-3">1. Accepted payment methods</h2>
          <div className="not-prose grid sm:grid-cols-2 gap-3 my-4">
            {METHODS.map((m) => (
              <div key={m.label} className="flex items-start gap-3 p-4 rounded-xl border border-neutral-200 bg-white">
                <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <m.icon className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-semibold text-ink text-sm">{m.label}</p>
                  <p className="text-xs text-ink/60 mt-0.5">{m.note}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-ink/70 text-sm leading-relaxed">
            International cards are supported, but the final amount is converted by your bank at their exchange rate. We do
            not accept cash, cheques, direct bank transfers, or off-platform UPI — those are unsafe and outside our records.
          </p>

          <h2 className="font-display text-2xl font-bold text-ink mt-8 mb-3">2. What triggers a payment</h2>
          <ul className="list-disc pl-6 text-ink/80 space-y-1.5">
            <li>Buying a credit pack to unlock owner contact details.</li>
            <li>Purchasing a monthly or yearly subscription plan.</li>
            <li>Adding a paid boost / featured slot to an individual listing.</li>
          </ul>
          <p className="text-ink/70 text-sm leading-relaxed mt-2 inline-flex items-start gap-2">
            <Info className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
            <span>Posting a basic listing is <strong>always free</strong>. You will never be charged just for signing up or browsing.</span>
          </p>

          <h2 className="font-display text-2xl font-bold text-ink mt-8 mb-3">3. Pricing, taxes & GST</h2>
          <ul className="list-disc pl-6 text-ink/80 space-y-1.5">
            <li>Every price shown on the <a href="/pricing" className="text-primary underline">Pricing</a> page is <strong>inclusive of applicable GST</strong> — no surprises at checkout.</li>
            <li>GST is charged at the prevailing Government of India rate for digital services (currently 18%).</li>
            <li>Businesses can add a GSTIN during checkout to receive a GST-compliant tax invoice.</li>
            <li>Prices may change from time to time. Any active subscription keeps its original price until the end of the current billing cycle.</li>
          </ul>

          <h2 className="font-display text-2xl font-bold text-ink mt-8 mb-3">4. Invoices & receipts</h2>
          <p className="text-ink/80 leading-relaxed">
            A payment receipt is emailed the moment your transaction succeeds. You can also download every invoice from{" "}
            <em>Dashboard → Transactions</em>. Each invoice includes:
          </p>
          <ul className="list-disc pl-6 text-ink/80 space-y-1.5 mt-2">
            <li>Order id, Razorpay payment id, and date.</li>
            <li>Item description (credits / plan / boost).</li>
            <li>Base price, GST amount, and total paid.</li>
            <li>Your GSTIN, if provided during checkout.</li>
          </ul>

          <h2 className="font-display text-2xl font-bold text-ink mt-8 mb-3">5. Security of your payment data</h2>
          <div className="not-prose flex items-start gap-3 p-4 rounded-xl border border-emerald-100 bg-emerald-50/50 my-4">
            <ShieldCheck className="h-5 w-5 text-emerald-700 mt-0.5 shrink-0" />
            <div className="text-sm text-ink/80">
              <p className="font-semibold text-ink">Your card never touches our servers.</p>
              <p className="mt-1">The entire card / UPI flow runs inside Razorpay&apos;s PCI-DSS certified environment. We only receive
              a payment id, an amount, and a success flag — enough to grant your credits and email you a receipt.</p>
            </div>
          </div>

          <h2 className="font-display text-2xl font-bold text-ink mt-8 mb-3">6. Failed & pending payments</h2>
          <ul className="list-disc pl-6 text-ink/80 space-y-1.5">
            <li>If a payment fails, no credits are added and you can retry immediately.</li>
            <li>If the money left your bank but the app says <em>pending</em>, please wait up to 30 minutes — most banks reconcile within that window.</li>
            <li>Still stuck? Share your bank reference number with us and we&apos;ll trace it with Razorpay.</li>
          </ul>

          <h2 className="font-display text-2xl font-bold text-ink mt-8 mb-3">7. Refunds & disputes</h2>
          <p className="text-ink/80 leading-relaxed inline-flex items-start gap-2">
            <ReceiptText className="h-4 w-4 mt-1 shrink-0 text-primary" />
            <span>All refund conditions live in our <a href="/refund" className="text-primary underline">Refund Policy</a>. For plan cancellations, see the <a href="/cancellation" className="text-primary underline">Cancellation Policy</a>. Please write to us before raising a bank chargeback — email is faster and cheaper for everyone.</span>
          </p>

          <h2 className="font-display text-2xl font-bold text-ink mt-8 mb-3">8. Contact for payment issues</h2>
          <p className="text-ink/80 leading-relaxed">
            Write to <a href="mailto:sales@thepataudi.com" className="text-primary underline">sales@thepataudi.com</a> with your
            Razorpay <code>pay_...</code> id, or call <a href="tel:9467458749" className="text-primary underline">+91 9467458749</a>.
            Please do not share your card number, CVV, or OTP with anyone — including us. We will never ask for them.
          </p>
        </article>
      </main>
      <Footer />
    </div>
  );
}
